import React, { useState, useEffect, useCallback } from "react";
import {
  Route,
  Routes,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { observer } from "mobx-react";
import { DepartmentDetailContainer } from "./DepartmentDetail.styled";
import { Overview, Users, KnowledgeBases, ActivityLog } from "./pages";
import { Header, Tabs } from "./components";
import { DepartmentDetailsTabsEnum } from "./enums/departmentDetailsTabs";
import { useStore } from "../../../../../hooks";
import { DepartmentDetailProvider, DepartmentUsersProvider } from "./context";
import {
  AssignUsersToGroupProvider,
} from "../../context";

const DepartmentDetail = ({ breadcrumbHelpers }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { departmentId } = useParams();
  const { departmentsStore } = useStore();
  const [selectedTab, setSelectedTab] = useState(
    DepartmentDetailsTabsEnum.OVERVIEW
  );

  const departmentName = departmentsStore.getDepartmentName();

  const fetchDepartment = useCallback(async () => {
    if (!departmentId) return;

    if (location.state?.departmentData) {
      departmentsStore.setCurrentDepartment(location.state.departmentData);
      return;
    }

    const departmentFromStore = departmentsStore.departmentList.find(
      (dept) => dept.department_id === departmentId || dept.id === departmentId
    );

    if (departmentFromStore) {
      departmentsStore.setCurrentDepartment(departmentFromStore);
      return;
    }

    if (
      departmentsStore.currentDepartment &&
      (departmentsStore.currentDepartment.department_id === departmentId ||
        departmentsStore.currentDepartment.id === departmentId)
    ) {
      return;
    }

    try {
      await departmentsStore.fetchDepartmentById(departmentId);
    } catch (error) {
      console.error("Error fetching department:", error);
    }
  }, [departmentId, location.state, departmentsStore]);

  useEffect(() => {
    fetchDepartment();
  }, [fetchDepartment]);

  const getTabRoute = (tabIndex) => {
    switch (tabIndex) {
      case DepartmentDetailsTabsEnum.OVERVIEW:
        return "overview";
      case DepartmentDetailsTabsEnum.USERS:
        return "users";
      case DepartmentDetailsTabsEnum.KNOWLEDGE_BASES:
        return "knowledge-bases";
      case DepartmentDetailsTabsEnum.ACTIVITY_LOG:
        return "activity-log";
      default:
        return "overview";
    }
  };

  const getTabFromRoute = (pathname) => {
    const pathParts = pathname.split("/").filter(Boolean);
    const departmentIndex = pathParts.indexOf("departments");

    if (departmentIndex === -1) {
      return DepartmentDetailsTabsEnum.OVERVIEW;
    }

    const segmentAfterDepartmentId = pathParts[departmentIndex + 2];

    if (segmentAfterDepartmentId === "overview") {
      return DepartmentDetailsTabsEnum.OVERVIEW;
    } else if (segmentAfterDepartmentId === "users") {
      return DepartmentDetailsTabsEnum.USERS;
    } else if (segmentAfterDepartmentId === "knowledge-bases") {
      return DepartmentDetailsTabsEnum.KNOWLEDGE_BASES;
    } else if (segmentAfterDepartmentId === "activity-log") {
      return DepartmentDetailsTabsEnum.ACTIVITY_LOG;
    } else {
      return DepartmentDetailsTabsEnum.OVERVIEW;
    }
  };

  useEffect(() => {
    const tabFromUrl = getTabFromRoute(location.pathname);
    setSelectedTab(tabFromUrl);

    const pathParts = location.pathname.split("/").filter(Boolean);
    const departmentIndex = pathParts.indexOf("departments");

    if (departmentIndex !== -1) {
      const segmentAfterDepartmentId = pathParts[departmentIndex + 2];

      if (!segmentAfterDepartmentId) {
        navigate(`/v2/departments/${departmentId}/overview`, { replace: true });
      }
    }
  }, [location.pathname, navigate, departmentId]);

  useEffect(() => {
    const tabFromUrl = getTabFromRoute(location.pathname);
    const tabLabel =
      tabFromUrl === DepartmentDetailsTabsEnum.OVERVIEW
        ? "Overview"
        : tabFromUrl === DepartmentDetailsTabsEnum.USERS
        ? "Users"
        : tabFromUrl === DepartmentDetailsTabsEnum.KNOWLEDGE_BASES
        ? "Knowledge Bases"
        : "Audit Log";

    const tabRoute = getTabRoute(tabFromUrl);
    const tabHref = `/v2/departments/${departmentId}/${tabRoute}`;

    breadcrumbHelpers?.setPageBreadcrumb?.(null, departmentName, {
      subHref: `/v2/departments/${departmentId}`,
      tabLabel: tabLabel,
      tabHref: tabHref,
    });
  }, [breadcrumbHelpers, location.pathname, departmentName, departmentId]);

  const handleTabChange = (index) => {
    setSelectedTab(index);
    const newRoute = getTabRoute(index);
    const basePath = `/v2/departments/${departmentId}`;

    navigate(`${basePath}/${newRoute}`, { replace: true });
  };

  const department = departmentsStore.currentDepartment;
  const groupUuid = department?.group_uuid || department?.groupUuid;

  return (
    <DepartmentDetailProvider>
      <DepartmentUsersProvider>
        <AssignUsersToGroupProvider>
          <DepartmentDetailContainer>
              <Header title={departmentName} />
              <Tabs
                key={selectedTab}
                onTabChange={handleTabChange}
                defaultIndex={selectedTab}
              />
              <Routes>
                <Route path="overview" element={<Overview />} />
                <Route
                  path="users"
                  element={
                    <Users departmentId={departmentId} groupUuid={groupUuid} />
                  }
                />
                <Route
                  path="knowledge-bases"
                  element={<KnowledgeBases departmentId={departmentId} />}
                />
                <Route
                  path="activity-log"
                  element={<ActivityLog departmentId={departmentId} />}
                />
              </Routes>
            </DepartmentDetailContainer>
        </AssignUsersToGroupProvider>
      </DepartmentUsersProvider>
    </DepartmentDetailProvider>
  );
};

export default observer(DepartmentDetail);
